import React, { useMemo, useEffect, useRef } from 'react';
import { View, StyleSheet, ActivityIndicator, Text } from 'react-native';
import * as echarts from 'echarts/core';
import { CandlestickChart, LineChart, BarChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  DataZoomComponent,
  LegendComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { Colors } from '@/constants/colors';
import { StockChartData } from '@/types/stock.types';
import type { ChartTimeframe } from '@/constants/chartTimeframes';
import type { ChartResolution } from '@/constants/chartResolutions';

// Register ECharts components for web
echarts.use([
  CanvasRenderer,
  CandlestickChart,
  LineChart,
  BarChart,
  TitleComponent,
  TooltipComponent,
  GridComponent,
  DataZoomComponent,
  LegendComponent,
]);

export interface ProfessionalCandlestickChartProps {
  symbol: string;
  chartData?: StockChartData[];
  isLoading?: boolean;
  timeframe?: ChartTimeframe;
  resolution?: ChartResolution;
  onReady?: () => void;
}

/**
 * Format date for X-axis based on timeframe
 */
const formatTimeLabel = (
  timestamp: number,
  timeframe?: ChartTimeframe,
  resolution?: ChartResolution
): string => {
  const ts = Number(timestamp);
  if (!Number.isFinite(ts)) return '';
  const date = new Date(ts);

  if (resolution && (resolution.endsWith('m') || resolution.endsWith('h'))) {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } else if (resolution === '1D' || resolution === '1W') {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }

  if (!timeframe || timeframe === '1D' || timeframe === '1W') {
    // Intraday: show time
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } else if (timeframe === '1M') {
    // Monthly: show date and time
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
    });
  } else {
    // Yearly: show date only
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }
};

/**
 * Transform StockChartData to ECharts candlestick format
 */
const transformToEChartsData = (data: StockChartData[]) => {
  return data.map(d => ({
    timestamp: new Date(d.date).getTime(),
    values: [d.open, d.close, d.low, d.high], // ECharts format: [open, close, low, high]
    volume: d.volume,
  }));
};

const ProfessionalCandlestickChart: React.FC<ProfessionalCandlestickChartProps> = ({
  symbol,
  chartData,
  isLoading,
  timeframe,
  resolution,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);

  const option = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return null;
    }

    const transformedData = transformToEChartsData(chartData);
    console.log(
      '[ProfessionalChart Web] ✅ Rendering chart with',
      transformedData.length,
      'candles'
    );

    const timestamps = transformedData.map(d => d.timestamp);
    const candlestickData = transformedData.map(d => d.values);
    const volumeData = transformedData.map(d => d.volume);

    // Calculate volume chart height (20% of total)
    const volumeHeight = 15;

    return {
      backgroundColor: Colors.background.primary,
      animation: false, // Disable for better performance
      grid: [
        {
          left: 10,
          right: 60,
          top: 40,
          height: `${100 - volumeHeight - 10}%`,
        },
        {
          left: 10,
          right: 60,
          top: `${100 - volumeHeight}%`,
          height: `${volumeHeight}%`,
        },
      ],
      xAxis: [
        {
          type: 'category',
          data: timestamps,
          gridIndex: 0,
          axisLine: {
            lineStyle: { color: Colors.border.default },
          },
          axisLabel: {
            color: Colors.text.tertiary,
            fontSize: 10,
            formatter: (value: any) => formatTimeLabel(Number(value), timeframe, resolution),
          },
          axisPointer: {
            show: true,
            lineStyle: { color: Colors.accent.primary, width: 1 },
          },
        },
        {
          type: 'category',
          data: timestamps,
          gridIndex: 1,
          axisLine: {
            lineStyle: { color: Colors.border.default },
          },
          axisLabel: {
            show: false,
          },
        },
      ],
      yAxis: [
        {
          type: 'value',
          scale: true,
          gridIndex: 0,
          position: 'right',
          axisLine: {
            lineStyle: { color: Colors.border.default },
          },
          axisLabel: {
            color: Colors.text.secondary,
            fontSize: 11,
            formatter: (value: number) => `$${value.toFixed(2)}`,
          },
          splitLine: {
            lineStyle: {
              color: Colors.border.subtle,
              type: 'dashed',
              opacity: 0.3,
            },
          },
        },
        {
          type: 'value',
          scale: true,
          gridIndex: 1,
          position: 'right',
          axisLine: {
            lineStyle: { color: Colors.border.default },
          },
          axisLabel: {
            color: Colors.text.tertiary,
            fontSize: 9,
            formatter: (value: number) => {
              if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
              if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
              return value.toString();
            },
          },
          splitLine: {
            show: false,
          },
        },
      ],
      dataZoom: [
        {
          type: 'inside',
          xAxisIndex: [0, 1],
          start: 0,
          end: 100,
          minValueSpan: 10,
        },
      ],
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          crossStyle: {
            color: Colors.accent.primary,
          },
        },
        backgroundColor: Colors.background.secondary,
        borderColor: Colors.border.default,
        borderWidth: 1,
        textStyle: {
          color: Colors.text.primary,
          fontSize: 12,
        },
        formatter: (params: any) => {
          const data = params[0];
          if (!data) return '';

          const timestamp = timestamps[data.dataIndex];
          const candle = candlestickData[data.dataIndex];
          const volume = volumeData[data.dataIndex];
          const [open, close, low, high] = candle;

          const date = new Date(timestamp);
          const dateStr = date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          });

          const change = close - open;
          const changePercent = (change / open) * 100;
          const changeColor = change >= 0 ? Colors.market.profit : Colors.market.loss;

          return `
            <div style="padding: 8px; min-width: 180px;">
              <div style="font-weight: 600; margin-bottom: 8px; color: ${Colors.text.primary};">${symbol}</div>
              <div style="font-size: 10px; color: ${Colors.text.tertiary}; margin-bottom: 8px;">${dateStr}</div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span style="color: ${Colors.text.secondary};">Open:</span>
                <span style="color: ${Colors.text.primary}; font-family: monospace;">$${open.toFixed(2)}</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span style="color: ${Colors.text.secondary};">High:</span>
                <span style="color: ${Colors.text.primary}; font-family: monospace;">$${high.toFixed(2)}</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span style="color: ${Colors.text.secondary};">Low:</span>
                <span style="color: ${Colors.text.primary}; font-family: monospace;">$${low.toFixed(2)}</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
                <span style="color: ${Colors.text.secondary};">Close:</span>
                <span style="color: ${Colors.text.primary}; font-family: monospace;">$${close.toFixed(2)}</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span style="color: ${Colors.text.secondary};">Change:</span>
                <span style="color: ${changeColor}; font-family: monospace;">${change >= 0 ? '+' : ''}$${change.toFixed(2)} (${changePercent >= 0 ? '+' : ''}${changePercent.toFixed(2)}%)</span>
              </div>
              <div style="display: flex; justify-content: space-between;">
                <span style="color: ${Colors.text.secondary};">Volume:</span>
                <span style="color: ${Colors.text.primary}; font-family: monospace;">${volume.toLocaleString()}</span>
              </div>
            </div>
          `;
        },
      },
      series: [
        {
          name: symbol,
          type: 'candlestick',
          data: candlestickData,
          xAxisIndex: 0,
          yAxisIndex: 0,
          itemStyle: {
            color: Colors.market.profit,
            color0: Colors.market.loss,
            borderColor: Colors.market.profit,
            borderColor0: Colors.market.loss,
            borderWidth: 1.5,
          },
        },
        {
          name: 'Volume',
          type: 'bar',
          data: volumeData,
          xAxisIndex: 1,
          yAxisIndex: 1,
          itemStyle: {
            color: (params: any) => {
              const candle = candlestickData[params.dataIndex];
              return candle[1] >= candle[0]
                ? Colors.market.profit + '40'
                : Colors.market.loss + '40';
            },
          },
        },
      ],
    };
  }, [chartData, symbol, timeframe]);

  // Initialize chart ONCE on mount
  useEffect(() => {
    if (!chartRef.current) {
      console.error('[ProfessionalChart] ❌ chartRef is null!');
      return;
    }

    console.log('[ProfessionalChart] 🎨 Creating chart instance (ONCE)...');

    try {
      chartInstanceRef.current = echarts.init(chartRef.current);
      console.log('[ProfessionalChart] ✅ Chart instance created');

      // Handle resize
      const handleResize = () => {
        chartInstanceRef.current?.resize();
      };

      window.addEventListener('resize', handleResize);

      return () => {
        console.log('[ProfessionalChart] 🧹 Cleaning up chart');
        window.removeEventListener('resize', handleResize);
        chartInstanceRef.current?.dispose();
        chartInstanceRef.current = null;
      };
    } catch (error) {
      console.error('[ProfessionalChart] ❌ Error creating chart:', error);
    }
  }, []); // Empty deps - run ONCE only

  // Update chart when option changes
  useEffect(() => {
    if (!chartInstanceRef.current || !option) {
      return;
    }

    console.log('[ProfessionalChart] 📊 Updating chart data...');

    try {
      chartInstanceRef.current.setOption(option, {
        notMerge: true, // Replace all data
      });
      console.log('[ProfessionalChart] ✅ Chart updated');
    } catch (error) {
      console.error('[ProfessionalChart] ❌ Error updating chart:', error);
    }
  }, [option]);

  // Always render container; overlay states below

  return (
    <div
      style={{
        backgroundColor: Colors.background.primary,
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: 320,
      }}
    >
      <div style={{ position: 'absolute', inset: 0 }}>
        <div
          ref={chartRef}
          style={{
            width: '100%',
            height: '100%',
            border: `1px solid ${Colors.border.default}`,
            backgroundColor: Colors.background.secondary,
          }}
        />
        {isLoading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(10, 14, 26, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: Colors.text.primary,
              fontSize: 14,
            }}
          >
            Loading {timeframe} data...
          </div>
        )}
        {!isLoading && (!chartData || chartData.length === 0 || !option) && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(10, 14, 26, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: Colors.text.primary,
              fontSize: 14,
            }}
          >
            Chart data unavailable
          </div>
        )}
      </div>
    </div>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.background.primary,
    width: '100%',
    height: 600,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.background.primary,
    padding: 40,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: Colors.text.secondary,
  },
  noChartData: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
    backgroundColor: Colors.background.primary,
  },
  noChartText: {
    fontSize: 14,
    color: Colors.text.tertiary,
  },
});

export default ProfessionalCandlestickChart;
